import React, { useState, useCallback, useMemo } from 'react'
import { Modal } from 'antd'
import { useIntl } from 'react-intl'

import { VotitoSurveyProps, SurveyResponse, SurveyQuestion } from './types'
import { isBlankAnswer } from './useVotitoSurvey'

// Votito rejects open-ended answers longer than this.
const VOTITO_MAX_EXPLANATION_LENGTH = 5000

import './VotitoSurvey.less'

const VotitoSurvey: React.FC<VotitoSurveyProps> = ({ survey, visible, onComplete, onDismiss }) => {
  const { formatMessage } = useIntl()
  const [currentStep, setCurrentStep] = useState(0)
  const [responses, setResponses] = useState<SurveyResponse[]>([])

  const totalQuestions = survey.questions.length
  const currentQuestion = survey.questions[currentStep]
  const isLastStep = currentStep === totalQuestions - 1

  const currentResponse = useMemo(() => responses.find((r) => r.questionId === currentQuestion?.id), [responses, currentQuestion?.id])
  const canContinue = currentQuestion?.type === 'statement' || Boolean(currentQuestion?.optional) || !isBlankAnswer(currentResponse?.value)

  // Steps passed, not answers given: skipped optional questions and statements add no response.
  const progressPct = totalQuestions > 0 ? Math.round((currentStep / totalQuestions) * 100) : 0

  const handleSelect = useCallback((questionId: string, value: string | number) => {
    setResponses((prev) => {
      const filtered = prev.filter((r) => r.questionId !== questionId)
      return [...filtered, { questionId, value }]
    })
  }, [])

  const handleToggle = useCallback((questionId: string, optionId: string) => {
    setResponses((prev) => {
      const existing = prev.find((r) => r.questionId === questionId)
      const selected = Array.isArray(existing?.value) ? (existing.value as string[]) : []
      const next = selected.includes(optionId) ? selected.filter((id) => id !== optionId) : [...selected, optionId]
      const filtered = prev.filter((r) => r.questionId !== questionId)
      return next.length > 0 ? [...filtered, { questionId, value: next }] : filtered
    })
  }, [])

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onComplete(responses)
    } else {
      setCurrentStep((prev) => prev + 1)
    }
  }, [isLastStep, responses, onComplete])

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }, [currentStep])

  const renderScaleQuestion = (question: SurveyQuestion) => {
    const min = question.scaleMin ?? 1
    const max = question.scaleMax ?? 7
    const count = max - min + 1
    const options = Array.from({ length: count }, (_, i) => min + i)

    return (
      <div className="vt-survey-scale">
        <div className="vt-survey-scale-labels">
          <span>{question.scaleMinLabel || formatMessage({ id: 'survey.stronglyDisagree' })}</span>
          <span>{question.scaleMaxLabel || formatMessage({ id: 'survey.stronglyAgree' })}</span>
        </div>
        <div className="vt-survey-scale-grid" style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}>
          {options.map((num) => {
            const isSelected = currentResponse?.value === num
            return (
              <button key={num} className={`vt-survey-scale-btn ${isSelected ? 'selected' : ''}`} onClick={() => handleSelect(question.id, num)}>
                <span className={`vt-survey-radio ${isSelected ? 'selected' : ''}`}>{isSelected && <span className="vt-survey-radio-dot" />}</span>
                {num}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  const renderSingleChoice = (question: SurveyQuestion) => {
    if (!question.options) return null
    return (
      <div className="vt-survey-choices">
        {question.options.map((option) => {
          const isSelected = currentResponse?.value === option.id
          return (
            <button key={option.id} className={`vt-survey-choice-btn ${isSelected ? 'selected' : ''}`} onClick={() => handleSelect(question.id, option.id)}>
              <span className={`vt-survey-radio ${isSelected ? 'selected' : ''}`}>{isSelected && <span className="vt-survey-radio-dot" />}</span>
              {option.label}
            </button>
          )
        })}
      </div>
    )
  }

  const renderMultiChoice = (question: SurveyQuestion) => {
    if (!question.options) return null
    const selected = Array.isArray(currentResponse?.value) ? (currentResponse.value as string[]) : []
    return (
      <div className="vt-survey-choices">
        {question.options.map((option) => {
          const isSelected = selected.includes(option.id)
          return (
            <button key={option.id} className={`vt-survey-choice-btn ${isSelected ? 'selected' : ''}`} onClick={() => handleToggle(question.id, option.id)}>
              <span className={`vt-survey-checkbox ${isSelected ? 'selected' : ''}`}>
                {isSelected && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1.5 5.5l2.5 2.5 4.5-5" />
                  </svg>
                )}
              </span>
              {option.label}
            </button>
          )
        })}
      </div>
    )
  }

  const renderTextQuestion = (question: SurveyQuestion) => {
    const value = (currentResponse?.value as string) || ''
    return (
      <div className="vt-survey-text">
        <textarea
          className="vt-survey-textarea"
          value={value}
          onChange={(e) => handleSelect(question.id, e.target.value)}
          placeholder={formatMessage({ id: 'survey.textPlaceholder' })}
          rows={4}
          maxLength={VOTITO_MAX_EXPLANATION_LENGTH}
        />
      </div>
    )
  }

  const renderQuestion = (question: SurveyQuestion) => {
    switch (question.type) {
      case 'scale':
        return renderScaleQuestion(question)
      case 'single-choice':
        return renderSingleChoice(question)
      case 'multi-choice':
        return renderMultiChoice(question)
      case 'text':
        return renderTextQuestion(question)
      case 'statement':
        return null
      default:
        return null
    }
  }

  if (!currentQuestion) return null

  return (
    <Modal open={visible} onCancel={onDismiss} footer={null} closable={false} width={620} centered destroyOnClose className="vt-survey-modal-wrapper">
      <div className="vt-survey-content">
        {/* Header */}
        <div className="vt-survey-header">
          <div className="vt-survey-brand">
            <span>{formatMessage({ id: 'survey.brandLabel' })}</span>
          </div>
          <button className="vt-survey-skip" onClick={onDismiss}>
            {formatMessage({ id: 'survey.skipForNow' })}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M3 3l8 8M11 3l-8 8" />
            </svg>
          </button>
        </div>

        {/* Question */}
        <div className="vt-survey-body">
          <h3 className="vt-survey-question-text">{currentQuestion.text}</h3>
          {currentQuestion.description && <div className="vt-survey-question-desc">{currentQuestion.description}</div>}
          <div className="vt-survey-question-content">{renderQuestion(currentQuestion)}</div>
        </div>

        {/* Actions */}
        <div className="vt-survey-actions">
          <div>
            {currentStep > 0 && (
              <button className="vt-survey-back-btn" onClick={handleBack}>
                {formatMessage({ id: 'survey.back' })}
              </button>
            )}
          </div>
          <div className="vt-survey-actions-right">
            <button className={`vt-survey-next-btn ${!canContinue ? 'disabled' : ''}`} disabled={!canContinue} onClick={handleNext}>
              {isLastStep ? formatMessage({ id: 'survey.submit' }) : formatMessage({ id: 'survey.next' })}
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="vt-survey-footer">
          <div className="vt-survey-progress-bar">
            <div className="vt-survey-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="vt-survey-progress-info">
            <span>{formatMessage({ id: 'survey.progressLabel' }, { current: currentStep + 1, total: totalQuestions })}</span>
            <span>{progressPct}%</span>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default VotitoSurvey
